<template>
  <div class="space-y-4">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h4 class="text-sm font-medium text-gray-900 dark:text-white">Routing Targets</h4>
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Configure load balancing targets with weights. Higher weights receive more traffic.
        </p>
      </div>
      <Button variant="outline" size="sm" @click="addTarget" :disabled="!hasAvailableModels">
        <svg class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 4v16m8-8H4"
          />
        </svg>
        Add Target
      </Button>
    </div>

    <!-- Targets List -->
    <div v-if="localTargets.length > 0" class="space-y-3">
      <Card v-for="(target, index) in localTargets" :key="index">
        <div class="flex items-start space-x-3">
          <!-- Target Number Badge -->
          <div
            class="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 flex items-center justify-center text-sm font-medium"
          >
            {{ index + 1 }}
          </div>

          <!-- Target Configuration -->
          <div class="flex-1 space-y-3">
            <!-- Backend Type Selector -->
            <div>
              <label
                :for="`backend-type-${index}`"
                class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Backend Type
              </label>
              <Select
                :id="`backend-type-${index}`"
                v-model="target.backend_type"
                :options="backendTypeOptions"
                @update:model-value="handleBackendTypeChange(index)"
                class="w-full"
              />
            </div>

            <!-- Model Selector -->
            <div>
              <label
                :for="`model-${index}`"
                class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Model
              </label>
              <Select
                :id="`model-${index}`"
                v-model="target.backend_id"
                :options="getModelOptions(target.backend_type)"
                placeholder="Select a model"
                class="w-full"
              />
              <p
                v-if="getModelOptions(target.backend_type).length === 0"
                class="mt-1 text-xs text-amber-600 dark:text-amber-400"
              >
                No {{ target.backend_type === 'base' ? 'base' : 'custom' }} models available. Please
                enable models first.
              </p>
            </div>

            <!-- Weight Input -->
            <div>
              <label
                :for="`weight-${index}`"
                class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Weight
              </label>
              <Input
                :id="`weight-${index}`"
                v-model.number="target.weight"
                type="number"
                :min="'1'"
                placeholder="Enter weight (minimum 1)"
                class="w-full"
              />
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Weight: {{ target.weight || 0 }} ({{ calculatePercentage(target.weight) }}% of
                traffic)
              </p>
            </div>

            <!-- Model Info (if selected) -->
            <div
              v-if="getSelectedModelInfo(target)"
              class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-2"
            >
              <div class="flex items-center justify-between text-xs">
                <span class="text-gray-500 dark:text-gray-400">Cost:</span>
                <span class="font-medium text-gray-900 dark:text-white">
                  ${{ getSelectedModelInfo(target)?.input_cost_1m?.toFixed(2) || '0.00' }} / ${{
                    getSelectedModelInfo(target)?.output_cost_1m?.toFixed(2) || '0.00'
                  }}
                  <span class="text-gray-500 dark:text-gray-400">/1M tokens</span>
                </span>
              </div>
            </div>
          </div>

          <!-- Remove Button -->
          <!-- eslint-disable-next-line asab-design-system/no-raw-button-card-markup -- icon-only remove-row action, not a standalone Button -->
          <Button
            variant="ghost"
            size="sm"
            @click="removeTarget(index)"
            class="flex-shrink-0 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </Button>
        </div>
      </Card>
    </div>

    <!-- Empty State -->
    <!-- eslint-disable-next-line asab-design-system/no-raw-button-card-markup -- dashed empty-state box, not a Card (no dashed/empty variant exists yet) -->
    <div
      v-else
      class="bg-gray-50 dark:bg-gray-800/50 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center"
    >
      <svg
        class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No routing targets</h3>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Add at least one routing target to configure load balancing.
      </p>
      <div class="mt-6">
        <Button variant="primary" size="sm" @click="addTarget" :disabled="!hasAvailableModels">
          <svg class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
          Add First Target
        </Button>
      </div>
      <p v-if="!hasAvailableModels" class="mt-3 text-xs text-amber-600 dark:text-amber-400">
        No models available. Please enable base models or create custom models first.
      </p>
    </div>

    <!-- Total Weight Summary -->
    <div
      v-if="localTargets.length > 0"
      class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3"
    >
      <div class="flex items-center justify-between">
        <span class="text-sm font-medium text-blue-900 dark:text-blue-300"> Total Weight </span>
        <span class="text-sm font-semibold text-blue-900 dark:text-blue-300">
          {{ totalWeight }}
        </span>
      </div>
      <p class="mt-1 text-xs text-blue-700 dark:text-blue-400">
        Traffic is distributed proportionally based on weights. Higher weights receive more
        requests.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { Button, Card, Input, Select } from '@asabdev/asab-ui';
  import type { RoutingTarget, BaseModelResponse, CustomModelResponse } from '../types';
  import type { SelectOption } from '@asabdev/asab-ui';

  interface Props {
    modelValue: RoutingTarget[];
    availableBaseModels: BaseModelResponse[];
    availableCustomModels: CustomModelResponse[];
  }

  interface Emits {
    (e: 'update:modelValue', value: RoutingTarget[]): void;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<Emits>();

  // Local state - create a mutable copy of the modelValue
  const localTargets = ref<RoutingTarget[]>([...props.modelValue]);

  // Backend type options
  const backendTypeOptions: SelectOption[] = [
    { value: 'base', label: 'Base Model' },
    { value: 'custom', label: 'Custom Model (BYOM)' },
  ];

  // Computed properties
  const hasAvailableModels = computed(() => {
    return props.availableBaseModels.length > 0 || props.availableCustomModels.length > 0;
  });

  const totalWeight = computed(() => {
    return localTargets.value.reduce((sum, target) => sum + (target.weight || 0), 0);
  });

  // Get model options based on backend type
  const getModelOptions = (backendType: 'base' | 'custom'): SelectOption[] => {
    if (backendType === 'base') {
      return props.availableBaseModels.map(model => ({
        value: model.name,
        label: `${model.name} (${model.provider})`,
      }));
    } else {
      return props.availableCustomModels.map(model => ({
        value: model.name,
        label: model.name,
      }));
    }
  };

  // Get selected model info for display
  const getSelectedModelInfo = (target: RoutingTarget) => {
    if (!target.backend_id) return null;

    if (target.backend_type === 'base') {
      return props.availableBaseModels.find(m => m.name === target.backend_id);
    } else {
      return props.availableCustomModels.find(m => m.name === target.backend_id);
    }
  };

  // Calculate percentage of traffic for a target
  const calculatePercentage = (weight: number): string => {
    if (!weight || totalWeight.value === 0) return '0';
    return ((weight / totalWeight.value) * 100).toFixed(1);
  };

  // Methods
  const addTarget = () => {
    const newTarget: RoutingTarget = {
      backend_id: '',
      backend_type: 'base',
      weight: 1,
    };
    localTargets.value.push(newTarget);
    emitUpdate();
  };

  const removeTarget = (index: number) => {
    localTargets.value.splice(index, 1);
    emitUpdate();
  };

  const handleBackendTypeChange = (index: number) => {
    // Reset backend_id when backend_type changes
    localTargets.value[index].backend_id = '';
    emitUpdate();
  };

  const emitUpdate = () => {
    emit('update:modelValue', [...localTargets.value]);
  };

  // Watch for changes in localTargets and emit updates
  watch(
    localTargets,
    () => {
      emitUpdate();
    },
    { deep: true }
  );

  // Watch for external changes to modelValue (e.g., form reset)
  watch(
    () => props.modelValue,
    newValue => {
      // Only update if the reference changed (not deep equality)
      if (newValue !== localTargets.value) {
        localTargets.value = [...newValue];
      }
    }
  );
</script>
